/**
 * Control de calidad global del dossier.
 *
 * Agrega los resultados de `sectionQuality` de todas las secciones del dossier
 * para dar un resumen ejecutivo: cuántos slides se generarán en total, cuántas
 * secciones tienen warnings y de qué severidad, qué secciones están vacías,
 * qué plantillas se usan más, etc.
 *
 * Pensado para alimentar un panel resumen arriba del editor del dossier
 * (DossierTab.jsx) que dé al usuario una visión global antes de presentar.
 */
import { sectionQuality } from "@/components/dossier/dossier_quality";

/**
 * Calcula el resumen global de calidad del dossier.
 *
 * @param {Array<Section>} sections - lista de secciones del dossier
 * @returns {{
 *   totalSections: number,
 *   sectionsWithContent: number,
 *   emptySections: number,
 *   totalSlides: number,
 *   counts: { ok, info, warn, error },
 *   warnings: Array<{ sectionKey, sectionLabel, level, code, message }>,
 *   bySection: Record<sectionKey, qualityResult>
 * }}
 */
export function dossierGlobalQuality(sections) {
  const list = sections || [];
  const totalSections = list.length;

  let totalSlides = 0;
  let emptySections = 0;
  const counts = { ok: 0, info: 0, warn: 0, error: 0 };
  const warnings = [];
  const bySection = {};

  for (const sec of list) {
    const result = sectionQuality(sec);
    bySection[sec.key] = result;

    if (result.level === "empty") {
      emptySections += 1;
      continue;
    }

    totalSlides += result.slides || 0;

    if (counts[result.level] !== undefined) counts[result.level] += 1;

    for (const w of result.warnings || []) {
      // Filtramos los "empty" de la lista de warnings ya que los reportamos
      // como contador propio (emptySections).
      if (w.level === "empty") continue;
      warnings.push({
        sectionKey: sec.key,
        sectionLabel: sec.label || sec.key,
        level: w.level,
        code: w.code,
        message: w.message,
      });
    }
  }

  return {
    totalSections,
    sectionsWithContent: totalSections - emptySections,
    emptySections,
    totalSlides,
    counts,
    warnings,
    bySection,
  };
}

/** Nivel global agregado del dossier completo. */
export function aggregateLevel(counts) {
  if (counts.error > 0) return "error";
  if (counts.warn > 0) return "warn";
  if (counts.info > 0) return "info";
  return "ok";
}
